import { Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../hooks/toolkit'
import Container from '../components/Container'

const PrivacyPolicy = () => {
  const { t } = useTranslation()
  usePageTitle('Privacy Policy | Agami')
  return (
    <Container direction="col" bgColorClass="bg-white">
      <div id="privacy-policy-container" className="w-full px-2 md:px-0">
        <Typography variant="h1">{t('privacy.title')}</Typography>
        <p>
          {t('privacy.intro')}{' '}
          <a href="mailto:dpo@mezzoforte.design" className="underline">
            dpo@mezzoforte.design
          </a>
          .
        </p>

        <p>{t('privacy.description')}</p>

        <h2>{t('privacy.definitions.title')}</h2>
        <p>
          <strong>{t('privacy.definitions.user')}:</strong> {t('privacy.definitions.userDesc')}
        </p>
        <p>
          <strong>{t('privacy.definitions.executableSoftware')}:</strong>{' '}
          {t('privacy.definitions.executableSoftwareDesc')}
        </p>
        <p>
          <strong>{t('privacy.definitions.platform')}:</strong> {t('privacy.definitions.platformDesc')}
        </p>

        <h2>{t('privacy.dataCollection.title')}</h2>

        <h3>{t('privacy.dataCollection.executableSoftware.title')}</h3>
        <p>{t('privacy.dataCollection.executableSoftware.intro')}</p>
        <ul>
          <li>
            {t('privacy.dataCollection.executableSoftware.softwareInfo.title')}
            <ul>
              <li>{t('privacy.dataCollection.executableSoftware.softwareInfo.version')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.softwareInfo.memoryUsage')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.softwareInfo.cpuUsage')}</li>
            </ul>
          </li>

          <li>
            {t('privacy.dataCollection.executableSoftware.audioProcess.title')}
            <ul>
              <li>{t('privacy.dataCollection.executableSoftware.audioProcess.healthStatus')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.audioProcess.activity')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.audioProcess.algorithmParams')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.audioProcess.audioMeasures')}</li>
            </ul>
          </li>

          <li>
            {t('privacy.dataCollection.executableSoftware.audioProcessing.title')}
            <ul>
              <li>{t('privacy.dataCollection.executableSoftware.audioProcessing.availableDevices')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.audioProcessing.devicesUsed')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.audioProcessing.statusInfo')}</li>
            </ul>
          </li>

          <li>{t('privacy.dataCollection.executableSoftware.networking')}</li>
          <li>{t('privacy.dataCollection.executableSoftware.gui')}</li>

          <li>
            {t('privacy.dataCollection.executableSoftware.operatingSystem.title')}
            <ul>
              <li>{t('privacy.dataCollection.executableSoftware.operatingSystem.name')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.operatingSystem.version')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.operatingSystem.localIp')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.operatingSystem.webIp')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.operatingSystem.timezone')}</li>
            </ul>
          </li>

          <li>
            {t('privacy.dataCollection.executableSoftware.computerBrand.title')}
            <ul>
              <li>{t('privacy.dataCollection.executableSoftware.computerBrand.cpuModel')}</li>
              <li>{t('privacy.dataCollection.executableSoftware.computerBrand.session')}</li>
            </ul>
          </li>

          <li>{t('privacy.dataCollection.executableSoftware.userIdentifier')}</li>
          <li>{t('privacy.dataCollection.executableSoftware.machineIdentifier')}</li>
        </ul>

        <h3>{t('privacy.dataCollection.platform.title')}</h3>
        <p>{t('privacy.dataCollection.platform.intro')}</p>
        <ul>
          <li>{t('privacy.dataCollection.platform.accountEmail')}</li>
          <li>{t('privacy.dataCollection.platform.accountName')}</li>
          <li>{t('privacy.dataCollection.platform.connectionLogs')}</li>
        </ul>

        <p>{t('privacy.dataCollection.platform.cookies')}</p>
        <ul>
          <li>{t('privacy.dataCollection.platform.localStorage.connectionToken')}</li>
          <li>{t('privacy.dataCollection.platform.localStorage.cachedData')}</li>
        </ul>

        <h2>{t('privacy.consent.title')}</h2>
        <p>{t('privacy.consent.description')}</p>

        <h2>{t('privacy.dataRequest.title')}</h2>
        <p>{t('privacy.dataRequest.description')}</p>

        <h2>{t('privacy.purpose.title')}</h2>
        <p>{t('privacy.purpose.intro')}</p>
        <ul>
          <li>{t('privacy.purpose.verifySoftware')}</li>
          <li>{t('privacy.purpose.improveSoftware')}</li>
          <li>{t('privacy.purpose.fineTuneVoice')}</li>
          <li>{t('privacy.purpose.improveTechnology')}</li>
        </ul>

        <h2>{t('privacy.retention.title')}</h2>
        <p>{t('privacy.retention.intro')}</p>
        <ul>
          <li>{t('privacy.retention.errorReports')}</li>
          <li>{t('privacy.retention.userDeletion')}</li>
          <li>{t('privacy.retention.expiredData')}</li>
          <li>{t('privacy.retention.audioStream')}</li>
        </ul>

        <h2>{t('privacy.communication.title')}</h2>
        <p>
          <strong>{t('privacy.communication.responsiblePeople')}:</strong>
        </p>

        <ul>
          <li>{t('privacy.communication.dpo')} · dpo@mezzoforte.design</li>
          <li>{t('privacy.communication.gdprCompliance')} · dpo@mezzoforte.design</li>
          <li>{t('privacy.communication.dataController')}</li>
        </ul>

        <h2>{t('privacy.thirdParties.title')}</h2>
        <p>{t('privacy.thirdParties.intro')}</p>
        <table>
          <thead>
            <tr>
              <th>{t('privacy.thirdParties.table.name')}</th>
              <th>{t('privacy.thirdParties.table.role')}</th>
              <th>{t('privacy.thirdParties.table.processedData')}</th>
              <th>{t('privacy.thirdParties.table.country')}</th>
              <th>{t('privacy.thirdParties.table.regulations')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>OVH SAS</td>
              <td>{t('privacy.thirdParties.ovh.role')}</td>
              <td>{t('privacy.thirdParties.ovh.processedData')}</td>
              <td>{t('privacy.thirdParties.ovh.country')}</td>
              <td>GDPR</td>
            </tr>
            <tr>
              <td>GitLab Inc</td>
              <td>{t('privacy.thirdParties.gitlab.role')}</td>
              <td>{t('privacy.thirdParties.gitlab.processedData')}</td>
              <td>{t('privacy.thirdParties.gitlab.country')}</td>
              <td>GDPR</td>
            </tr>
          </tbody>
        </table>

        <p>{t('privacy.thirdParties.except')}</p>

        <h2>{t('privacy.dataBreach.title')}</h2>
        <p>{t('privacy.dataBreach.intro')}</p>
        <p>
          <strong>{t('privacy.dataBreach.authority')}</strong>
        </p>
        <p>8 rue Vivienne, CS 30223</p>
        <p>F-75002 Paris, Cedex 02</p>
        <p>Tel. +33 1 53 73 22 22</p>
        <p>Fax +33 1 53 73 22 00</p>
        <p>
          <a href="https://www.cnil.fr/">https://www.cnil.fr/</a>
        </p>
      </div>
    </Container>
  )
}

export default PrivacyPolicy
